import React, { useState, useEffect, memo, useRef } from 'react';
import styled from 'styled-components';
import Pagination from './Pagination';
import BlockList from './BlockList';
import KeyEvent from './KeyEvent';
import Infographic from './Infographic';
import { setup, breakPoints } from '../config';
import { getTargetPage, getMaxPage, autoScroll } from '../utils/index';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';

const { updateInterval, maxContentWidth } = setup;

const _ = {
  isEqual: _isEqual,
  get: _get,
};

const params = {
  method: 'GET',
  cors: 'cors',
  cache: 'no-store',
  headers: {
    'Content-Type': 'application/json',
  },
}

const urls = [
  `${setup.cdnUrlBase}/content.json`,
  `${setup.cdnUrlBase}/key-events.json`
]

const Container = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  justify-content: center;
  margin: 0 auto;
  box-sizing: border-box;
  max-width: ${maxContentWidth};
  > aside {
    flex: 0 0 40%;
    padding: 0 16px;
    box-sizing: border-box;
  }
  > main {
    flex: 0 0 60%;
    padding: 0 16px;
    box-sizing: border-box;
  }
  @media(max-width: ${breakPoints.maxMobile}px) {
    flex-direction: column;
  }
`

function UpperHalf({
  location,
  history,
}) {
  const upperHalf = useRef(null);
  const [ state, setState ] = useState({
    page: 1,
    blockList: [],
    targetId: null,
    keyEventList: [],
  });

  const { page, blockList, targetId, keyEventList } = state;

  useEffect(() => {
    const { search } = location;
    const searchParams = new URLSearchParams(search);
    let targetId = +searchParams.get('id') || null;
    Promise.all(urls.map((url, index) => {
      return fetch(url)
              .then((response) => {
                return response.json();
              })
              .then((data) => data)
              .catch((e) => {
                console.log('e: ', e)
              })
    }))
    .then((value) => {
      let targetPage = +searchParams.get('page') || 1;
      const blockList = value[0] || [];
      const maxPage = getMaxPage({ numberOfBlocks:  blockList.length });
      if (targetPage > maxPage) {
        targetPage = maxPage;
        window.history.replaceState(null, '',`/?page=${targetPage}`);
      }
      if (targetId) {
        targetPage = getTargetPage({
          targetId,
          blockList,
        });
        if (!targetPage) {
          window.history.replaceState(null, '',`/`);
          targetPage = 1;
          targetId = null;
        }
      }
      setState(state => ({
        ...state,
        page: targetPage,
        blockList,
        keyEventList: value[1] || [],
        targetId,
      }));
    })
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      Promise.all(urls.map((url, index) => {
        return fetch(url)
                .then(async (response) => {
                  return await response.json();
                })
      }))
      .then((value) => {
        const { blockList, keyEventList } = state;
        let content, keyEvent;
        [ content, keyEvent ] = value;
        let blockComparison = _.isEqual(blockList, content);
        let takeComparison = _.isEqual(keyEventList, keyEvent);
        if (!blockComparison && !takeComparison) {
          console.log('update both')
          setState(state => ({
            ...state,
            blockList: content,
            keyEventList: keyEvent,
          }))
        } else if (!takeComparison) {
          console.log('update keyEvent')
          setState(state => ({
            ...state,
            keyEventList: keyEvent,
          }))
        } else if (!blockComparison) {
          console.log('update block')
          setState(state => ({
            ...state,
            blockList: content,
          }))
        } else {
          console.log('update nothing')
        }
      })
    }, updateInterval)

    return () => {
      clearInterval(interval);
    }
  }, [state]);

  /*
  useEffect(() => {
    const events = new EventSource('/events/subscribe')
    events.onmessage = (event) => {
      const { content, keyEvent } = JSON.parse(event.data);
      const { blockList, keyEventList } = state;
      let blockComparison = _.isEqual(blockList, content);
      let takeComparison = _.isEqual(keyEventList, keyEvent);
      if (!blockComparison && !takeComparison) {
        console.log('update both')
        setState(state => ({
          ...state,
          blockList: content,
          keyEventList: keyEvent,
        }))
      } else if (!takeComparison) {
        console.log('update keyEvent')
        setState(state => ({
          ...state,
          keyEventList: keyEvent,
        }))
      } else if (!blockComparison) {
        console.log('update block')
        setState(state => ({
          ...state,
          blockList: content,
        }))
      } else {
        console.log('update nothing')
      }
    };
    return () => {
      events.onmessage = ()=> {};
      events.close();
    }
  }, [state])
  */

  const onClickEvent= ({
    targetId: localTargetId,
  }) => {
    const targetPage = getTargetPage({
      targetId: localTargetId,
      blockList,
    });
    let ifIdExist = false;
    for (let i = 0 ; i < blockList.length; i+=1) {
      if (blockList[i].timestamp === localTargetId) {
        ifIdExist = true;
        break;
      } else {
        continue;
      }
    }
    if (localTargetId !== targetId && ifIdExist) {
      setState(state => ({
        ...state,
        page: targetPage,
        targetId: localTargetId,
      }));
      window.history.replaceState(null, '',`/?id=${localTargetId}`);
    }
  }

  const onClickPage = ({
    targetPage
  }) => {
    if (targetPage !== page) {
      setState(state => ({
        ...state,
        page: targetPage,
        targetId: null,
      }));
      window.history.replaceState(null, '',`/?page=${targetPage}`);
    }
  }

  useEffect(() => {
    if (!targetId) {
      let offset = 0;
      const infoElem = document && document.getElementById('infographic');
      const startIndex = (page - 1) * setup.maxNumberOfEvents;
      const endIndex = startIndex + setup.maxNumberOfEvents;
      const slicedData = blockList.slice(startIndex, endIndex);
      const currentTargetId = _.get(slicedData, `[0].timestamp`, null);
      if (infoElem) {
        offset = _.get(infoElem, 'offsetHeight', 0);
      }
      autoScroll({
        offset,
        targetId: currentTargetId,
      })
    }
  }, [page, targetId])

  const getUpperHalfRef = () => {
    return upperHalf.current;
  }

  const startIndex = (page - 1) * setup.maxNumberOfEvents;
  const endIndex = startIndex + setup.maxNumberOfEvents;
  const slicedData = blockList.slice(startIndex, endIndex);
  return (
    <Container
      ref={upperHalf}
    >
      <aside>
        <Infographic
          getUpperHalfRef={getUpperHalfRef}
        />
      </aside>
      <main>
        <KeyEvent
          total={blockList.length}
          keyEventList={keyEventList}
          blockList={blockList}
          onClickEvent={onClickEvent}
          targetId={targetId}
        />
        <BlockList
          slicedData={slicedData}
          blockList={blockList}
          targetId={targetId}
          location={location}
        />
        <Pagination
          total={blockList.length}
          page={page}
          onClickPage={onClickPage}
        />
      </main>
    </Container>
  );
}

export default memo(UpperHalf);
